/**
 * Generic page boot loader.
 *
 * Sequential, fail-fast execution of an ordered list of dependencies. Mirrors
 * the semantics of `RW-ERP-Service/src/components/common/PageDependencyLoader.vue`
 * with three deliberate additions:
 *
 *   1. **Retry** — a single button reruns the failed step (and any pending
 *      ones after it). Vue forces a full reload.
 *   2. **Skip-if-cached** — a dependency may return `{ status: 'success',
 *      cached: true }` to indicate it satisfied itself from cache. The
 *      `isAllCached` flag lets the UI component avoid rendering at all
 *      when nothing actually had to wait.
 *   3. **AbortSignal** — every action receives a cancellation signal that
 *      fires on unmount or retry, so long fetches don't leak.
 *
 * The hook owns no domain knowledge — every "what does this step do"
 * concern is encapsulated inside the caller-supplied `Dependency` objects.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DEPENDENCY_STEP_TIMEOUT_MS } from '../constants/timing';
import {
  Dependency,
  DependencyContext,
  DependencyResult,
  DependencyStepState,
} from '../dependencies/types';
import { logger } from '../utils/logger';

export type LoaderStatus = 'idle' | 'running' | 'complete' | 'error';

export interface LoaderError {
  stepId: string;
  stepTitle: string;
  message: string;
}

export interface UseDependencyLoaderResult {
  status: LoaderStatus;
  steps: ReadonlyArray<DependencyStepState>;
  /** Index of the step currently running. -1 when idle / complete / errored. */
  currentIndex: number;
  /** Populated only when `status === 'error'`. */
  error: LoaderError | null;
  /** Re-runs from the first non-completed step. */
  retry: () => void;
  /**
   * True only when the loader has reached `complete` AND every successful
   * step reported `cached: true`. UI can use this to skip rendering the
   * loader entirely on warm boots.
   */
  isAllCached: boolean;
}

export interface UseDependencyLoaderOptions {
  /** Run immediately on mount (default true). Set false to start manually via `retry`. */
  autoStart?: boolean;
  /** Per-step timeout. Defaults to `DEPENDENCY_STEP_TIMEOUT_MS`. */
  stepTimeoutMs?: number;
  /**
   * Minimum wall-clock time (ms) the loader stays in the `running` state
   * before transitioning to `complete`. When deps resolve faster than
   * this (e.g. all from cache), the `complete` transition is delayed so
   * the timeline UI is visible for at least this duration. Zero or
   * undefined means no artificial delay (current behavior).
   */
  minDisplayMs?: number;
  onComplete?: (steps: ReadonlyArray<DependencyStepState>) => void;
  onError?: (err: LoaderError) => void;
}

const buildInitialSteps = (
  deps: ReadonlyArray<Dependency>,
): ReadonlyArray<DependencyStepState> =>
  deps.map((d) => ({
    id: d.id,
    title: d.title,
    status: 'pending',
    cached: false,
  }));

const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  abort: AbortController,
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      abort.abort();
      reject(new Error(`Step timed out after ${ms}ms`));
    }, ms);
    promise
      .then((v) => {
        window.clearTimeout(timer);
        resolve(v);
      })
      .catch((err: unknown) => {
        window.clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      });
  });

/**
 * Module-scope step-state updater extracted to reduce nesting depth (S2004).
 * Maps the steps array, marking the step at `errorIndex` as errored.
 */
const applyStepError = (
  steps: ReadonlyArray<DependencyStepState>,
  errorIndex: number,
  errorMessage: string,
): ReadonlyArray<DependencyStepState> =>
  steps.map((s, idx) =>
    idx === errorIndex ? { ...s, status: 'error', error: errorMessage } : s,
  );

export function useDependencyLoader(
  dependencies: ReadonlyArray<Dependency>,
  options: UseDependencyLoaderOptions = {},
): UseDependencyLoaderResult {
  const {
    autoStart = true,
    stepTimeoutMs = DEPENDENCY_STEP_TIMEOUT_MS,
    minDisplayMs,
    onComplete,
    onError,
  } = options;

  const [steps, setSteps] = useState<ReadonlyArray<DependencyStepState>>(() =>
    buildInitialSteps(dependencies),
  );
  const [status, setStatus] = useState<LoaderStatus>(
    autoStart ? 'running' : 'idle',
  );
  const [currentIndex, setCurrentIndex] = useState<number>(autoStart ? 0 : -1);
  const [error, setError] = useState<LoaderError | null>(null);

  // Latest deps reference — used so the run loop sees additions without
  // restarting on every render (we only restart on retry or autostart).
  const depsRef = useRef(dependencies);
  depsRef.current = dependencies;

  // The active AbortController for the in-flight run. Replaced on retry,
  // aborted on unmount.
  const runControllerRef = useRef<AbortController | null>(null);
  // Monotonically increasing token so a stale run can detect it's been
  // superseded and bail out cleanly.
  const runTokenRef = useRef(0);

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  const runFrom = useCallback(
    async (startIndex: number) => {
      const token = ++runTokenRef.current;
      const controller = new AbortController();
      runControllerRef.current = controller;
      const runStartedAt = Date.now();

      const deps = depsRef.current;
      // Reset state for steps from startIndex onward.
      setSteps((prev) =>
        prev.map((s, i) =>
          i < startIndex
            ? s
            : { id: s.id, title: s.title, status: 'pending', cached: false },
        ),
      );
      setError(null);
      setStatus('running');
      setCurrentIndex(startIndex);

      const results: Record<string, DependencyResult> = {};

      // Per-step helpers extracted so the run loop's cognitive complexity
      // stays within Sonar's threshold (S3776). No behaviour change.
      const executeStep = async (
        dep: Dependency,
        ctx: DependencyContext,
      ): Promise<DependencyResult | null> => {
        if (dep.enabled && !dep.enabled(ctx)) return null;
        try {
          return await withTimeout(dep.action(ctx), stepTimeoutMs, controller);
        } catch (err) {
          return {
            status: 'error',
            message: err instanceof Error ? err.message : String(err),
          };
        }
      };

      const reportFailure = (i: number, dep: Dependency, outcome: DependencyResult): void => {
        const failure: LoaderError = {
          stepId: dep.id,
          stepTitle: dep.title,
          message: outcome.message ?? 'An unknown error occurred',
        };
        setSteps((prev) => applyStepError(prev, i, failure.message));
        setStatus('error');
        setError(failure);
        setCurrentIndex(-1);
        onErrorRef.current?.(failure);
        logger.warn('[useDependencyLoader] step failed', failure);
      };

      for (let i = startIndex; i < deps.length; i += 1) {
        if (runTokenRef.current !== token || controller.signal.aborted) return;
        const dep = deps[i];
        setCurrentIndex(i);
        setSteps((prev) =>
          prev.map((s, idx) => (idx === i ? { ...s, status: 'running' } : s)),
        );

        const ctx: DependencyContext = {
          signal: controller.signal,
          results,
        };

        const outcome = await executeStep(dep, ctx);
        if (outcome === null) {
          results[dep.id] = { status: 'skipped' };
          setSteps((prev) =>
            prev.map((s, idx) =>
              idx === i ? { ...s, status: 'skipped' } : s,
            ),
          );
          continue;
        }

        if (runTokenRef.current !== token) return;
        results[dep.id] = outcome;

        if (outcome.status === 'error') {
          reportFailure(i, dep, outcome);
          return;
        }

        setSteps((prev) =>
          prev.map((s, idx) =>
            idx === i
              ? {
                  ...s,
                  status: outcome.status,
                  cached: Boolean(outcome.cached),
                }
              : s,
          ),
        );
      }

      if (runTokenRef.current !== token) return;

      const finalize = (): void => {
        if (runTokenRef.current !== token) return;
        setStatus('complete');
        setCurrentIndex(-1);
        // Read latest steps via the setter callback to avoid a stale snapshot.
        setSteps((prev) => {
          onCompleteRef.current?.(prev);
          return prev;
        });
      };

      const elapsed = Date.now() - runStartedAt;
      const remaining = (minDisplayMs ?? 0) - elapsed;

      if (remaining > 0) {
        const delayTimer = window.setTimeout(finalize, remaining);
        // If the run is superseded or aborted before the delay expires,
        // the stale-token check inside `finalize` prevents state writes.
        // Clean up the timer on abort to avoid dangling timeouts.
        const onAbort = (): void => window.clearTimeout(delayTimer);
        controller.signal.addEventListener('abort', onAbort, { once: true });
      } else {
        finalize();
      }
    },
    [stepTimeoutMs, minDisplayMs],
  );

  const retry = useCallback(() => {
    runControllerRef.current?.abort();
    // Resume from the first non-completed step (or the failed one).
    const firstIncomplete = steps.findIndex(
      (s) => s.status !== 'success' && s.status !== 'skipped',
    );
    void runFrom(firstIncomplete === -1 ? 0 : firstIncomplete);
  }, [runFrom, steps]);

  // Reset step list when the dependency array changes shape (new ids/length).
  // Object identity changes alone don't trigger this — only structural diffs.
  const depsSignature = useMemo(
    () => dependencies.map((d) => d.id).join('|'),
    [dependencies],
  );

  useEffect(() => {
    setSteps(buildInitialSteps(depsRef.current));
    setError(null);
    if (autoStart) {
      void runFrom(0);
    } else {
      setStatus('idle');
      setCurrentIndex(-1);
    }
    return () => {
      runControllerRef.current?.abort();
      runTokenRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsSignature, autoStart]);

  const isAllCached = useMemo(
    () =>
      status === 'complete' &&
      steps.every((s) => s.status === 'skipped' || (s.status === 'success' && s.cached)),
    [status, steps],
  );

  return { status, steps, currentIndex, error, retry, isAllCached };
}
